import { VehicleType, TrailerType, Currency, FuelUnit } from '../types';
import { convertCurrency, getCurrencySymbol } from './currencyUtils';

// Inflation factor - this will affect all cost-related values
// 1.0 means no inflation, 1.05 means 5% inflation, etc.
const INFLATION_FACTOR = 1.0;

// Conversion constant: 1 gallon = 3.78541 liters
const LITERS_PER_GALLON = 3.78541;

// Helper function to apply inflation to a value
const applyInflation = (value: number): number => {
  return value * INFLATION_FACTOR;
};

// Constants for cost calculation
const CONSTANTS = {
  // Trips per month by vehicle type
  TRIPS_PER_MONTH: {
    PICKUP_TRUCK: 48,
    DUAL_WHEEL_PICKUP: 48,
    RABON: 36,
    TORTON: 36,
    SINGLE_TRAILER: 24,
    FULL_TRAILER: 12
  },
  
  // Asset values in MXN - These will be affected by inflation
  ASSET_VALUES: {
    // Trailers
    DRY_BOX: applyInflation(450000),
    REFRIGERATED_BOX: applyInflation(650000), // Higher value due to refrigeration equipment
    PLATFORM: applyInflation(400000),
    CONTAINER_CARRIER: applyInflation(400000),
    GRAIN_CAGE: applyInflation(520000),
    GRAIN_HOPPER: applyInflation(520000),
    PRESSURIZED_HOPPER: applyInflation(1200000),
    LIQUID_TANK: applyInflation(600000), // Higher value due to specialized equipment
    LOWBOY: applyInflation(500000),
    DOLLY: applyInflation(280000),
    
    // Vehicles
    TRACTOR: applyInflation(2500000),
    PICKUP_TRUCK: applyInflation(1200000),
    DUAL_WHEEL_PICKUP: applyInflation(1400000),
    RABON: applyInflation(1800000),
    TORTON: applyInflation(2000000)
  },
  
  // Depreciation parameters
  DEPRECIATION_MONTHS: 60,
  RECOVERY_VALUE_FACTOR: 0.30,
  
  // Insurance costs per month (in MXN) - Affected by inflation
  INSURANCE_MONTHLY_COST: {
    PICKUP_TRUCK: applyInflation(2500),    // $2000/month ≈ $52/trip
    DUAL_WHEEL_PICKUP: applyInflation(2500), // $2500/month ≈ $52/trip
    RABON: applyInflation(4200),          // $3200/month ≈ $116/trip
    TORTON: applyInflation(4200),         // $3500/month ≈ $116/trip
    SINGLE_TRAILER: applyInflation(7000),  // $4000/month ≈ $295/trip
    FULL_TRAILER: applyInflation(8500)    // $5000/month ≈ $694/trip
  },
  
  // GPS costs (in MXN) - Affected by inflation
  GPS_MONTHLY_COST: {
    NO_TRAILER: applyInflation(800),
    SINGLE_TRAILER: applyInflation(1000),
    FULL_TRAILER: applyInflation(1200)
  },
  
  // Registration costs (in MXN) - Affected by inflation
  REGISTRATION_COSTS: {
    MOTOR_VEHICLE: applyInflation(5000),
    TRACTOR: applyInflation(10000),
    TRAILER: applyInflation(5000),
    FULL_PERMIT: applyInflation(5000)
  },
  REGISTRATION_MONTHS: 60,
  
  // Verification costs (in MXN) - Affected by inflation
  MECHANICAL_VERIFICATION: {
    COST: applyInflation(600),
    MONTHS: 12
  },
  
  EMISSION_VERIFICATION: {
    COST: applyInflation(600),
    ANNUAL_VERIFICATIONS: 2,
    MONTHS: 12
  },
  
  // Driver salary base by distance range (in MXN) - Affected by inflation
  DRIVER_SALARY_BASE: {
    50: applyInflation(1000),      // Local very short routes (0-50km)
    100: applyInflation(1500),     // Local short routes (51-100km)
    150: applyInflation(1700),     // Local medium routes (101-150km)
    200: applyInflation(1800),     // Local long routes (151-200km)
    250: applyInflation(2000),    // Regional short routes (201-250km)
    300: applyInflation(2500),    // Regional medium routes (251-300km)
    350: applyInflation(3000),    // Regional long routes (301-350km)
    400: applyInflation(3500),    // National short routes (351-400km)
    450: applyInflation(4000),    // National medium routes (401-450km)
    500: applyInflation(4500),    // National medium-long routes (451-500km)
    550: applyInflation(5000),    // National long routes (501-550km)
    600: applyInflation(5500),    // National very long routes (551-600km)
    650: applyInflation(6000),    // National extended routes (601-650km)
    700: applyInflation(6500),    // International short routes (651-700km)
    750: applyInflation(7000),    // International medium routes (701-750km)
    800: applyInflation(7500),    // International medium-long routes (751-800km)
    850: applyInflation(8000),    // International long routes (801-850km)
    900: applyInflation(8500),    // International very long routes (851-900km)
    950: applyInflation(9000),    // International extended routes (901-950km)
    1000: applyInflation(9500),   // Long distance routes (951-1000km)
    1500: applyInflation(10000),   // Long distance routes (1001-1500km)
    2000: applyInflation(12000),   // Long distance routes (1501-2000km)
    2500: applyInflation(15000),   // Long distance routes (2001-2500km)
    3000: applyInflation(17000),   // Long distance routes (2501-3000km)
    3500: applyInflation(20000),  // Long distance routes (3001-3500km)
    4000: applyInflation(25000),  // Long distance routes (3501-4000km)
    4500: applyInflation(30000),  // Long distance routes (4001-4500km)
    5000: applyInflation(40000)   // Long distance routes (4501-5000km)
  },
  
  // Salary factors by vehicle type
  SALARY_FACTOR: {
    PICKUP_TRUCK: 0.5,
    DUAL_WHEEL_PICKUP: 0.6,
    RABON: 0.7,
    TORTON: 0.8,
    SINGLE_TRAILER: 1.0,
    FULL_TRAILER: 1.5
  },
  
  // Extra km rate by vehicle type (in MXN) - Affected by inflation
  EXTRA_KM_RATE: {
    PICKUP_TRUCK: applyInflation(5),
    DUAL_WHEEL_PICKUP: applyInflation(6),
    RABON: applyInflation(7),
    TORTON: applyInflation(8),
    SINGLE_TRAILER: applyInflation(10),
    FULL_TRAILER: applyInflation(15)
  },
  
  // Driver benefits rate (percentage of base salary)
  DRIVER_BENEFITS_RATE: 0.35,
  
  // Monthly maintenance costs by vehicle type (in MXN) - Affected by inflation
  MAINTENANCE_COST: {
    PICKUP_TRUCK: applyInflation(20000),
    DUAL_WHEEL_PICKUP: applyInflation(20000),
    RABON: applyInflation(30000),
    TORTON: applyInflation(30000),
    SINGLE_TRAILER: applyInflation(50000),
    FULL_TRAILER: applyInflation(70000)
  },
  
  // Tire costs (in MXN) - Affected by inflation
  TIRE_PRICE: applyInflation(8000),
  TIRE_LIFE_KM: 250000,
  
  // Number of tires by vehicle type
  TIRE_COUNT: {
    PICKUP_TRUCK: 4,
    DUAL_WHEEL_PICKUP: 6,
    RABON: 6,
    TORTON: 10,
    SINGLE_TRAILER_2_AXLES: 18,
    SINGLE_TRAILER_3_AXLES: 22,
    FULL_TRAILER_6_AXLES: 22,
    FULL_TRAILER_7_AXLES: 26,
    FULL_TRAILER_8_AXLES: 30,
    FULL_TRAILER_9_AXLES: 34
  },
  
  // Fuel efficiency by vehicle type (km/liter) when LOADED
  FUEL_EFFICIENCY: {
    PICKUP_TRUCK: 4.5,
    DUAL_WHEEL_PICKUP: 4.5,
    RABON: 4.2,
    TORTON: 3.2,
    SINGLE_TRAILER: 2.3,
    FULL_TRAILER: 1.6
  },

  // Fuel efficiency by vehicle type (km/liter) when EMPTY
  // Values are 1 point higher than when loaded
  FUEL_EFFICIENCY_EMPTY: {
    PICKUP_TRUCK: 6.0,
    DUAL_WHEEL_PICKUP: 6.0,
    RABON: 5.5,
    TORTON: 4.5,
    SINGLE_TRAILER: 3.5,
    FULL_TRAILER: 2.6
  }
};

// Vehículos que no requieren remolque
const VEHICLES_WITHOUT_TRAILER = [
  VehicleType.PICKUP_TRUCK,
  VehicleType.DUAL_WHEEL_PICKUP,
  VehicleType.RABON,
  VehicleType.TORTON
];

function getVehicleCategory(vehicleType: VehicleType): string {
  if (vehicleType === VehicleType.PICKUP_TRUCK) {
    return 'PICKUP_TRUCK';
  } else if (vehicleType === VehicleType.DUAL_WHEEL_PICKUP) {
    return 'DUAL_WHEEL_PICKUP';
  } else if (vehicleType === VehicleType.RABON) {
    return 'RABON';
  } else if (vehicleType === VehicleType.TORTON) {
    return 'TORTON';
  } else if (
    vehicleType === VehicleType.SINGLE_WITH_2_AXLE_TRAILER || 
    vehicleType === VehicleType.SINGLE_WITH_3_AXLE_TRAILER
  ) {
    return 'SINGLE_TRAILER';
  } else {
    return 'FULL_TRAILER';
  }
}

function getTrailerCategory(trailerType: TrailerType | null): string {
  if (!trailerType) {
    return 'NO_TRAILER';
  }
  
  switch (trailerType) {
    case TrailerType.DRY_BOX:
      return 'DRY_BOX';
    case TrailerType.REFRIGERATED_BOX:
      return 'REFRIGERATED_BOX';
    case TrailerType.PLATFORM:
      return 'PLATFORM';
    case TrailerType.CONTAINER_CARRIER:
      return 'CONTAINER_CARRIER';
    case TrailerType.GRAIN_CAGE:
      return 'GRAIN_CAGE';
    case TrailerType.GRAIN_HOPPER:
      return 'GRAIN_HOPPER';
    case TrailerType.PRESSURIZED_HOPPER:
      return 'PRESSURIZED_HOPPER';
    case TrailerType.LIQUID_TANK:
      return 'LIQUID_TANK';
    case TrailerType.LOWBOY:
      return 'LOWBOY';
    default:
      return 'DRY_BOX'; // Default fallback
  }
}

function getTireCount(vehicleType: VehicleType): number {
  if (vehicleType === VehicleType.PICKUP_TRUCK) {
    return CONSTANTS.TIRE_COUNT.PICKUP_TRUCK;
  } else if (vehicleType === VehicleType.DUAL_WHEEL_PICKUP) {
    return CONSTANTS.TIRE_COUNT.DUAL_WHEEL_PICKUP;
  } else if (vehicleType === VehicleType.RABON) {
    return CONSTANTS.TIRE_COUNT.RABON;
  } else if (vehicleType === VehicleType.TORTON) {
    return CONSTANTS.TIRE_COUNT.TORTON;
  } else if (vehicleType === VehicleType.SINGLE_WITH_2_AXLE_TRAILER) {
    return CONSTANTS.TIRE_COUNT.SINGLE_TRAILER_2_AXLES;
  } else if (vehicleType === VehicleType.SINGLE_WITH_3_AXLE_TRAILER) {
    return CONSTANTS.TIRE_COUNT.SINGLE_TRAILER_3_AXLES;
  } else if (vehicleType === VehicleType.FULL_6_AXLES) {
    return CONSTANTS.TIRE_COUNT.FULL_TRAILER_6_AXLES;
  } else if (vehicleType === VehicleType.FULL_7_AXLES) {
    return CONSTANTS.TIRE_COUNT.FULL_TRAILER_7_AXLES;
  } else if (vehicleType === VehicleType.FULL_8_AXLES) {
    return CONSTANTS.TIRE_COUNT.FULL_TRAILER_8_AXLES;
  } else {
    return CONSTANTS.TIRE_COUNT.FULL_TRAILER_9_AXLES;
  }
}

function calculateDepreciation(vehicleType: VehicleType, trailerType: TrailerType | null): number {
  const vehicleCategory = getVehicleCategory(vehicleType);
  const trailerCategory = getTrailerCategory(trailerType);
  const tripsPerMonth = CONSTANTS.TRIPS_PER_MONTH[vehicleCategory];
  
  let totalAssetValue = 0;
  
  if (vehicleCategory === 'PICKUP_TRUCK') {
    totalAssetValue = CONSTANTS.ASSET_VALUES.PICKUP_TRUCK;
  } else if (vehicleCategory === 'DUAL_WHEEL_PICKUP') {
    totalAssetValue = CONSTANTS.ASSET_VALUES.DUAL_WHEEL_PICKUP;
  } else if (vehicleCategory === 'RABON') {
    totalAssetValue = CONSTANTS.ASSET_VALUES.RABON;
  } else if (vehicleCategory === 'TORTON') {
    totalAssetValue = CONSTANTS.ASSET_VALUES.TORTON;
  } else if (vehicleCategory === 'SINGLE_TRAILER' && trailerType) {
    totalAssetValue = CONSTANTS.ASSET_VALUES.TRACTOR + CONSTANTS.ASSET_VALUES[trailerCategory];
  } else if (vehicleCategory === 'FULL_TRAILER' && trailerType) {
    totalAssetValue = CONSTANTS.ASSET_VALUES.TRACTOR + 
                     (CONSTANTS.ASSET_VALUES[trailerCategory] * 2) + 
                     CONSTANTS.ASSET_VALUES.DOLLY;
  }
  
  const depreciableValue = totalAssetValue * (1 - CONSTANTS.RECOVERY_VALUE_FACTOR);
  const monthlyDepreciation = depreciableValue / CONSTANTS.DEPRECIATION_MONTHS;
  
  return monthlyDepreciation / tripsPerMonth;
}

function calculateInsurance(vehicleType: VehicleType): number {
  const vehicleCategory = getVehicleCategory(vehicleType);
  const tripsPerMonth = CONSTANTS.TRIPS_PER_MONTH[vehicleCategory];
  return CONSTANTS.INSURANCE_MONTHLY_COST[vehicleCategory] / tripsPerMonth;
}

function calculateGPS(vehicleType: VehicleType): number {
  const vehicleCategory = getVehicleCategory(vehicleType);
  const tripsPerMonth = CONSTANTS.TRIPS_PER_MONTH[vehicleCategory];
  
  let gpsCost = CONSTANTS.GPS_MONTHLY_COST.NO_TRAILER;
  if (vehicleCategory === 'SINGLE_TRAILER') {
    gpsCost = CONSTANTS.GPS_MONTHLY_COST.SINGLE_TRAILER;
  } else if (vehicleCategory === 'FULL_TRAILER') {
    gpsCost = CONSTANTS.GPS_MONTHLY_COST.FULL_TRAILER;
  }
  
  return gpsCost / tripsPerMonth;
}

function calculateRegistration(vehicleType: VehicleType, trailerType: TrailerType | null): number {
  const vehicleCategory = getVehicleCategory(vehicleType);
  const tripsPerMonth = CONSTANTS.TRIPS_PER_MONTH[vehicleCategory];
  
  let registrationCost = CONSTANTS.REGISTRATION_COSTS.MOTOR_VEHICLE;
  if (vehicleCategory === 'SINGLE_TRAILER' && trailerType) {
    registrationCost = CONSTANTS.REGISTRATION_COSTS.TRACTOR + CONSTANTS.REGISTRATION_COSTS.TRAILER;
  } else if (vehicleCategory === 'FULL_TRAILER' && trailerType) {
    registrationCost = CONSTANTS.REGISTRATION_COSTS.TRACTOR + 
                      (CONSTANTS.REGISTRATION_COSTS.TRAILER + CONSTANTS.REGISTRATION_COSTS.FULL_PERMIT) * 2;
  }
  
  return registrationCost / (CONSTANTS.REGISTRATION_MONTHS * tripsPerMonth);
}

function calculateMechanicalVerification(vehicleType: VehicleType, trailerType: TrailerType | null): number {
  const vehicleCategory = getVehicleCategory(vehicleType);
  const tripsPerMonth = CONSTANTS.TRIPS_PER_MONTH[vehicleCategory];
  
  let verificationCount = 1;
  if (vehicleCategory === 'SINGLE_TRAILER' && trailerType) {
    verificationCount = 2;
  } else if (vehicleCategory === 'FULL_TRAILER' && trailerType) {
    verificationCount = 4;
  }
  
  return (CONSTANTS.MECHANICAL_VERIFICATION.COST * verificationCount) / 
         (CONSTANTS.MECHANICAL_VERIFICATION.MONTHS * tripsPerMonth);
}

function calculateEmissionVerification(vehicleType: VehicleType): number {
  const vehicleCategory = getVehicleCategory(vehicleType);
  const tripsPerMonth = CONSTANTS.TRIPS_PER_MONTH[vehicleCategory];
  
  return (CONSTANTS.EMISSION_VERIFICATION.COST * CONSTANTS.EMISSION_VERIFICATION.ANNUAL_VERIFICATIONS) / 
         (CONSTANTS.EMISSION_VERIFICATION.MONTHS * tripsPerMonth);
}

function calculateDriverSalary(distance: number, vehicleType: VehicleType): number {
  const vehicleCategory = getVehicleCategory(vehicleType);
  
  const distanceRanges = Object.keys(CONSTANTS.DRIVER_SALARY_BASE)
    .map(Number)
    .sort((a, b) => a - b);
  
  let baseRange = distanceRanges[0];
  for (const range of distanceRanges) {
    if (distance <= range) {
      baseRange = range;
      break;
    }
  }
  
  const baseSalary = CONSTANTS.DRIVER_SALARY_BASE[baseRange];
  const salaryFactor = CONSTANTS.SALARY_FACTOR[vehicleCategory];
  let salary = baseSalary * salaryFactor;
  
  if (distance > baseRange) {
    const extraKmRate = CONSTANTS.EXTRA_KM_RATE[vehicleCategory];
    const extraDistance = distance - baseRange;
    salary += extraDistance * extraKmRate;
  }
  
  return salary;
}

function calculateDriverBenefits(driverSalary: number): number {
  return driverSalary * CONSTANTS.DRIVER_BENEFITS_RATE;
}

function calculateMaintenance(vehicleType: VehicleType): number {
  const vehicleCategory = getVehicleCategory(vehicleType);
  const tripsPerMonth = CONSTANTS.TRIPS_PER_MONTH[vehicleCategory];
  return CONSTANTS.MAINTENANCE_COST[vehicleCategory] / (tripsPerMonth);
}

function calculateTireCost(distance: number, vehicleType: VehicleType): number {
  const tireCount = getTireCount(vehicleType);
  return (CONSTANTS.TIRE_PRICE * tireCount * distance) / CONSTANTS.TIRE_LIFE_KM;
}

// Modified function to support "empty" load status and fuel unit
function calculateFuelCost(
  distance: number,
  vehicleType: VehicleType,
  fuelPrice: number,
  isLoaded: boolean = true,
  fuelUnit: FuelUnit = FuelUnit.LITER
): number {
  const vehicleCategory = getVehicleCategory(vehicleType);

  // Choose appropriate fuel efficiency based on load status
  const fuelEfficiency = isLoaded
    ? CONSTANTS.FUEL_EFFICIENCY[vehicleCategory]
    : CONSTANTS.FUEL_EFFICIENCY_EMPTY[vehicleCategory];

  // Convert fuel price to price per liter if it's in gallons
  const pricePerLiter = fuelUnit === FuelUnit.GALLON
    ? fuelPrice / LITERS_PER_GALLON
    : fuelPrice;

  return (distance / fuelEfficiency) * pricePerLiter;
}

export function calculateDetailedTransportCost(
  distance: number,
  vehicleType: VehicleType,
  trailerType: TrailerType | null,
  fuelPrice: number,
  isLoaded: boolean = true,
  fuelUnit: FuelUnit = FuelUnit.LITER
): { totalCost: number, breakdown: Record<string, number> } {
  // Calculate fixed costs (per trip)
  const depreciation = calculateDepreciation(vehicleType, trailerType);
  const insurance = calculateInsurance(vehicleType);
  const gps = calculateGPS(vehicleType);
  const registration = calculateRegistration(vehicleType, trailerType);
  const mechanicalVerification = calculateMechanicalVerification(vehicleType, trailerType);
  const emissionVerification = calculateEmissionVerification(vehicleType);
  const maintenance = calculateMaintenance(vehicleType);
  
  // Calculate variable costs based on one-way distance
  const driverSalary = calculateDriverSalary(distance, vehicleType);
  const driverBenefits = calculateDriverBenefits(driverSalary);
  
  // Calculate variable costs based on round trip distance
  const roundTripDistance = distance * 2;
  const tireCost = calculateTireCost(roundTripDistance, vehicleType);
  // Use load status for fuel efficiency and fuel unit
  const fuelCost = calculateFuelCost(roundTripDistance, vehicleType, fuelPrice, isLoaded, fuelUnit);
  
  let tollCost = 0;
  
  const costBreakdown = {
    depreciation,
    insurance,
    gps,
    registration,
    mechanicalVerification,
    emissionVerification,
    driverSalary,
    driverBenefits,
    maintenance,
    tireCost,
    fuelCost,
    tollCost
  };
  
  // Round all costs up to the nearest integer
  const roundedBreakdown: Record<string, number> = {};
  for (const [key, value] of Object.entries(costBreakdown)) {
    roundedBreakdown[key] = Math.ceil(value);
  }
  
  const totalCost = Object.values(roundedBreakdown).reduce((a, b) => a + b, 0);
  
  return {
    totalCost,
    breakdown: roundedBreakdown
  };
}

// Modified function to support multi-segment routes with mixed load statuses
export function calculateMultiSegmentTransportCost(
  segments: { distance: number; loadStatus: 'loaded' | 'empty' }[],
  vehicleType: VehicleType,
  trailerType: TrailerType | null,
  fuelPrice: number,
  fuelUnit: FuelUnit = FuelUnit.LITER
): { totalCost: number, breakdown: Record<string, number> } {
  let totalDistance = 0;
  let totalFuelCost = 0;
  let totalTireCost = 0;
  
  // Calculate total distance for driver salary
  totalDistance = segments.reduce((sum, segment) => sum + segment.distance, 0);
  
  // Calculate fuel and tire costs for each segment (double for round trip)
  segments.forEach(segment => {
    const roundTripDistance = segment.distance * 2;
    const isLoaded = segment.loadStatus === 'loaded';
    
    // Calculate fuel cost for this segment with appropriate efficiency and fuel unit
    totalFuelCost += calculateFuelCost(
      roundTripDistance,
      vehicleType,
      fuelPrice,
      isLoaded,
      fuelUnit
    );
    
    // Calculate tire cost (this doesn't change based on load status)
    totalTireCost += calculateTireCost(roundTripDistance, vehicleType);
  });
  
  // Fixed costs are the same regardless of load status
  const depreciation = calculateDepreciation(vehicleType, trailerType);
  const insurance = calculateInsurance(vehicleType);
  const gps = calculateGPS(vehicleType);
  const registration = calculateRegistration(vehicleType, trailerType);
  const mechanicalVerification = calculateMechanicalVerification(vehicleType, trailerType);
  const emissionVerification = calculateEmissionVerification(vehicleType);
  const maintenance = calculateMaintenance(vehicleType);
  
  // Variable costs based on total one-way distance
  const driverSalary = calculateDriverSalary(totalDistance, vehicleType);
  const driverBenefits = calculateDriverBenefits(driverSalary);
  
  let tollCost = 0; // Tolls handled separately
  
  const costBreakdown = {
    depreciation,
    insurance,
    gps,
    registration,
    mechanicalVerification,
    emissionVerification,
    driverSalary,
    driverBenefits,
    maintenance,
    tireCost: totalTireCost,
    fuelCost: totalFuelCost,
    tollCost
  };
  
  // Round all costs up to the nearest integer
  const roundedBreakdown: Record<string, number> = {};
  for (const [key, value] of Object.entries(costBreakdown)) {
    roundedBreakdown[key] = Math.ceil(value);
  }
  
  const totalCost = Object.values(roundedBreakdown).reduce((a, b) => a + b, 0);
  
  return {
    totalCost,
    breakdown: roundedBreakdown
  };
}

export function calculateTransportCost(
  distance: number,
  vehicleType: VehicleType,
  trailerType: TrailerType | null,
  fuelPrice: number,
  isLoaded: boolean = true,
  fuelUnit: FuelUnit = FuelUnit.LITER
): number {
  const result = calculateDetailedTransportCost(distance, vehicleType, trailerType, fuelPrice, isLoaded, fuelUnit);
  return result.totalCost;
}

export function formatCurrency(amount: number, currency: Currency = Currency.MXN): string {
  // Get the currency symbol
  const symbol = getCurrencySymbol(currency);
  
  // Format the amount according to the currency's conventions
  return `${symbol}${Math.ceil(amount).toLocaleString('es-MX', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;
}

export function calculateCostWithTax(cost: number, tax: number): number {
  // If tax is 0, return original cost
  if (tax === 0) return cost;
  // Calculate cost without tax (assuming tax is already included in 'cost')
  // cost / (1 + tax/100)
  return cost / (1 + tax / 100);
}